import {useEffect, useMemo, useRef} from 'react';
import {useRouterHistory, useRouterLocation} from 'coreUtils/adapters/router';

import {useRoute} from 'coreHooks';
import {getConfig, isServer} from 'coreUtils/utils';
import {PageType} from 'coreUtils/IUtils';
import {useScrollRestoration} from 'coreComponents/ScrollRestoration';
import {registerModuleStateReset} from 'coreUtils/moduleState';

let justLanded = true;

registerModuleStateReset(() => {
	justLanded = true;
});

export default () => {
	const routerLocation = useRouterLocation();
	const scrollRestorationContext = useScrollRestoration(true);
	const pathnameDeps = scrollRestorationContext ?
		[scrollRestorationContext?.routeChanged]
		: [routerLocation.pathname, routerLocation.search];
	const {memoizedPathname, memoizedSearch} = useMemo(() => {
		return {
			memoizedPathname: routerLocation.pathname,
			memoizedSearch: routerLocation.search
		};
	}, pathnameDeps);
	const routerHistory = useRouterHistory();
	const config = getConfig();
	const instanceOrigin = `${config.app.instanceProtocol}://${config.app.instanceHostname}`;
	const route = useRoute();
	const hasNavigated = useRef(false);

	// Subscribed once: the React Router 7 adapter returns a new history object on every navigation, and
	// subscribing again would miss the notification of that navigation (`RouterListenerBridge` notifies in an
	// effect that runs before the one of the page): `isPop` would stay false (THP-13905).
	useEffect(() => {
		return routerHistory.listen(() => {
			hasNavigated.current = true;
		});
	}, []);

	const isPop = routerHistory.action === 'POP' && !!routerLocation.key && hasNavigated.current;

	/**
	 * Since we modify the url also via standard browser API, useLocation doesn't return always the correct values
	 */
	const location = useMemo(() => {
		if (isServer) {
			return {
				...routerLocation,
				origin: instanceOrigin,
			};
		}

		return {
			pathname: decodeURIComponent(memoizedPathname),
			origin: routerLocation.origin,
			search: memoizedSearch,
			hash: routerLocation.hash,
			key: routerLocation.key,
			href: `${memoizedPathname}${memoizedSearch}`,
		};
	}, [routerLocation, memoizedPathname, memoizedSearch, instanceOrigin]);

	const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

	useEffect(() => {
		justLanded = false;
	}, [route.currentRoute]);

	return useMemo(
		() =>
			({
				justLanded,
				...route,
				...location,
				searchParams,
				navigationAction: routerHistory.action as 'POP' | 'PUSH' | 'REPLACE',
				isPop,
			}) as ReturnType<typeof useRouterLocation> &
				typeof route & {
				justLanded: boolean;
				href: string;
				searchParams: URLSearchParams;
				pageType?: PageType;
				is404: boolean;
				isPDP: boolean;
				navigationAction: 'POP' | 'PUSH' | 'REPLACE';
				isPop: boolean;
			},
		[justLanded, route, location, searchParams, routerHistory.action, isPop],
	);
};
